/**
 * Script entry for browser <script src="..."> usage.
 * Exposes UsageAnalytics on globalThis (window). Auto-inits from config in the script tag (data-config) or window.__ANALYTICS_CONFIG__.
 */
import { init, track, identify, flush, reset, isInitialized } from './facade.js';
import type { AnalyticsInitConfig } from './types/config.js';

declare global {
  interface Window {
    UsageAnalytics: {
      init: typeof init;
      track: typeof track;
      identify: typeof identify;
      flush: typeof flush;
      reset: typeof reset;
      isInitialized: typeof isInitialized;
    };
    __ANALYTICS_CONFIG__?: AnalyticsInitConfig;
  }
}

const api = {
  init,
  track,
  identify,
  flush,
  reset,
  isInitialized,
};

if (typeof globalThis !== 'undefined') {
  (globalThis as unknown as { UsageAnalytics: typeof api }).UsageAnalytics = api;
}

function getConfigFromScriptTag(): AnalyticsInitConfig | null {
  if (typeof document === 'undefined') return null;
  const script = document.currentScript as HTMLScriptElement | null;
  if (!script) return null;
  const raw = script.getAttribute('data-config');
  if (!raw || typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw) as AnalyticsInitConfig;
    if (parsed && typeof parsed.appId === 'string' && parsed.appId.trim() !== '') {
      return parsed;
    }
  } catch {
    // Invalid JSON
  }
  return null;
}

function autoInit(): void {
  if (typeof window === 'undefined') return;
  const config = getConfigFromScriptTag() ?? window.__ANALYTICS_CONFIG__;
  if (config) {
    init(config).catch(() => {
      // Silent fail for auto-init; app can call init() manually with onError if needed
    });
  }
}

autoInit();
